import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Check,
  X,
  ArrowRight,
  MapPin,
  MessageSquareText,
  Clock,
  Shield,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  problemContent,
  cryptoContent,
  howItWorksSteps,
  trustContent,
  plansData,
} from "@/data/mock";

const JOIN_URL = "https://app.lifely.guide/join/pricing";

const fadeInUp = {
  hidden: { opacity: 0, y: 26 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`py-24 md:py-32 ${className}`}>
    <div className="max-w-6xl mx-auto px-6">{children}</div>
  </section>
);

/* ---- Problem ---- */
const ProblemSection = () => (
  <Section>
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
      >
        <motion.h2
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
          className="font-serif text-3xl md:text-4xl lg:text-[2.8rem] text-foreground leading-tight"
        >
          {problemContent.heading}
        </motion.h2>
        <motion.p
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
          className="mt-6 text-lg text-muted-foreground leading-relaxed"
        >
          {problemContent.body}
        </motion.p>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="relative"
      >
        <div className="rounded-2xl overflow-hidden shadow-2xl shadow-foreground/10">
          <img
            src="https://images.unsplash.com/photo-1517554558809-9b4971b38f39?auto=format&fit=crop&w=900&q=80"
            alt="Hands using a tablet for planning"
            className="w-full h-[360px] object-cover"
          />
        </div>
        {/* Decorative accent */}
        <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-xl bg-primary/10 -z-10" />
        <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-secondary -z-10" />
      </motion.div>
    </div>
  </Section>
);

/* ---- How It Works Teaser ---- */
const HowItWorksTeaser = () => (
  <Section className="bg-secondary/30">
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-3xl mx-auto"
    >
      <p className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4">
        Discover
      </p>
      <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
        Three powerful ways people use Lifely
      </h2>
      <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
        Whether you're preparing for a will, maintaining one, or tracking a growing portfolio — 
        Lifely helps you create structure around your life.
      </p>
      <Link to="/how-it-works">
        <Button 
          variant="outline" 
          className="rounded-full h-11 px-8 font-medium"
        >
          See how it works <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </motion.div>
  </Section>
);

/* ---- Crypto ---- */
const CryptoSection = () => (
  <Section className="bg-secondary/30">
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      {/* Content - Left */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight"
        >
          {cryptoContent.heading}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mt-6 text-lg text-muted-foreground leading-relaxed"
        >
          {cryptoContent.body}
        </motion.p>
        <motion.ul 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-8 space-y-4"
        >
          {cryptoContent.bullets.map((bullet, i) => (
            <li
              key={i}
              className="flex items-start gap-4"
            >
              <div className="mt-1 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="text-foreground/80 text-lg">{bullet}</span>
            </li>
          ))}
        </motion.ul>
      </motion.div>

      {/* Image - Right */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="relative aspect-square rounded-2xl overflow-hidden shadow-xl">
          <img
            src="/images/crypto-coins.jpg"
            alt="Cryptocurrency coins"
            className="w-full h-full object-cover"
          />
        </div>
      </motion.div>
    </div>
  </Section>
);

/* ---- Image Break ---- */
const ImageBreak = () => (
  <div className="relative h-[300px] md:h-[400px] overflow-hidden">
    <img
      src="https://images.unsplash.com/photo-1640958903837-ce7bc6060501?auto=format&fit=crop&w=1920&q=80"
      alt="Aerial view of coastline"
      className="w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/30" />
  </div>
);

/* ---- How It Works ---- */
const HowItWorksSection = () => {
  const icons = [MapPin, MessageSquareText, Clock];
  return (
    <Section>
      <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
        {/* Left: Mindful image accent */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-2 rounded-2xl overflow-hidden shadow-xl hidden lg:block"
        >
          <img
            src="https://images.unsplash.com/photo-1630484507146-cbd5eef1a400?auto=format&fit=crop&w=800&q=80"
            alt="Person meditating in calm setting"
            className="w-full h-[460px] object-cover"
          />
        </motion.div>

        {/* Right: Steps */}
        <div className="lg:col-span-3">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
            className="mb-12"
          >
            <motion.p
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4"
            >
              How it works
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl text-foreground"
            >
              Three steps to clarity
            </motion.h2>
          </motion.div>

          <div className="space-y-10">
            {howItWorksSteps.map((step, i) => {
              const Icon = icons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, duration: 0.6 }}
                  className="flex gap-6"
                >
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-secondary flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-medium text-primary/50 uppercase tracking-wider">
                        Step {step.number}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ---- Trust ---- */
const TrustSection = () => (
  <section className="relative py-24 md:py-32 overflow-hidden">
    {/* Background image */}
    <div className="absolute inset-0">
      <img
        src="https://images.unsplash.com/photo-1490730141103-6cac27aaab94?auto=format&fit=crop&w=1920&q=80"
        alt=""
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-background/85" />
    </div>
    <div className="relative z-10 max-w-6xl mx-auto px-6">
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="text-center mb-14"
    >
      <h2 className="font-serif text-3xl md:text-4xl text-foreground">
        {trustContent.heading}
      </h2>
    </motion.div>

    <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-card/95 backdrop-blur-sm rounded-2xl border border-border/50 p-8"
      >
        <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-7 font-medium">
          Lifely is
        </h3>
        <ul className="space-y-5">
          {trustContent.is.map((item, i) => (
            <li key={i} className="flex items-center gap-3.5">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Check className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="text-foreground">{item}</span>
            </li>
          ))}
        </ul>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="bg-card/95 backdrop-blur-sm rounded-2xl border border-border/50 p-8"
      >
        <h3 className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mb-7 font-medium">
          Lifely is not
        </h3>
        <ul className="space-y-5">
          {trustContent.isNot.map((item, i) => (
            <li key={i} className="flex items-center gap-3.5">
              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <span className="text-muted-foreground">{item}</span>
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
    </div>
  </section>
);

/* ---- Plans Teaser ---- */
const PlansTeaser = () => (
  <Section>
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={stagger}
      className="text-center mb-14"
    >
      <motion.h2
        variants={fadeInUp}
        className="font-serif text-3xl md:text-4xl text-foreground"
      >
        Start small. Upgrade when you're ready.
      </motion.h2>
      <motion.p
        variants={fadeInUp}
        className="mt-4 text-muted-foreground text-lg"
      >
        Choose the level that fits your needs.
      </motion.p>
    </motion.div>

    <div className="grid md:grid-cols-3 gap-6">
      {plansData.map((plan, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.1, duration: 0.6 }}
        >
          <Card
            className={`h-full ${
              plan.highlighted
                ? "border-primary/25 shadow-lg ring-1 ring-primary/10"
                : "border-border/60"
            } bg-card hover:shadow-md transition-shadow duration-300`}
          >
            <CardContent className="p-7">
              <h3 className="font-semibold text-foreground">{plan.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {plan.tagline}
              </p>
              <div className="mt-5">
                <span className="text-3xl font-serif text-foreground">
                  {plan.price}
                </span>
                {plan.priceNote && (
                  <span className="text-sm text-muted-foreground ml-1">
                    {plan.priceNote}
                  </span>
                )}
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                {plan.description}
              </p>
              <a href={JOIN_URL}>
                <Button
                  variant={plan.highlighted ? "default" : "outline"}
                  className={`w-full mt-6 rounded-full ${
                    plan.highlighted
                      ? "bg-foreground text-background hover:bg-foreground/85"
                      : ""
                  }`}
                >
                  View details <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </a>
            </CardContent>
          </Card>
        </motion.div>
      ))}
    </div>
  </Section>
);

/* ---- Final CTA ---- */
const FinalCTA = () => (
  <section className="relative py-28 md:py-36 overflow-hidden">
    {/* Background image */}
    <div className="absolute inset-0">
      <img
        src="https://images.unsplash.com/photo-1649583946384-1e8388c4091f?auto=format&fit=crop&w=1920&q=80"
        alt=""
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#2D2A26]/65" />
    </div>
    <div className="max-w-6xl mx-auto px-6 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="text-center max-w-2xl mx-auto"
      >
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight text-white">
          Ready to map your life with less complexity?
        </h2>
        <p className="mt-5 text-white/60 text-lg">
          Start with free Insights, or go straight to mapping.
        </p>
        <a href={JOIN_URL}>
          <Button className="mt-9 h-13 px-10 bg-white text-[#2D2A26] hover:bg-white/90 rounded-full text-sm font-medium">
            Join <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </a>
      </motion.div>
    </div>
  </section>
);

/* ---- Export ---- */
export const HomeSections = () => (
  <>
    <ProblemSection />
    <HowItWorksTeaser />
    <CryptoSection />
    <HowItWorksSection />
    <TrustSection />
    <PlansTeaser />
    <FinalCTA />
  </>
);
